import { RefObject, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { PER_PAGE_OPTIONS } from '../constants'

interface BookListPaginationProps {
    // Pagination
    currentPage: number
    totalPages: number
    itemsPerPage: number
    onPageChange: (page: number) => void
    onItemsPerPageChange: (count: number) => void

    // Info display
    startIndex: number
    endIndex: number
    totalItems: number

    // Export (conditional, only for own library list views)
    showExport?: boolean
    selectedCount?: number
    filteredCount?: number
    showExportOptions?: boolean
    setShowExportOptions?: (show: boolean) => void
    onExport?: (format: 'csv' | 'json') => void
    onImportClick?: () => void
    onFileUpload?: (e: ChangeEvent<HTMLInputElement>) => void
    fileInputRef?: RefObject<HTMLInputElement>
}

export default function BookListPagination({
    // Pagination
    currentPage,
    totalPages,
    itemsPerPage,
    onPageChange,
    onItemsPerPageChange,

    // Info display
    startIndex,
    endIndex,
    totalItems,

    // Export (conditional, only for own library list views)
    showExport = false,
    selectedCount = 0,
    filteredCount = 0,
    showExportOptions = false,
    setShowExportOptions,
    onExport,
    onImportClick,
    onFileUpload,
    fileInputRef,
}: Readonly<BookListPaginationProps>) {
    const { t } = useTranslation()

    return (
        <>
            {/* Pagination Controls */}
            {totalPages > 1 && (
                <div className="pagination-controls">
                    <div className="pagination-info">
                        {t('bookList.pagination.showing', {
                            start: startIndex + 1,
                            end: Math.min(endIndex, totalItems),
                            total: totalItems,
                        })}
                    </div>
                    <div className="pagination-buttons">
                        <button className="pagination-btn" onClick={() => onPageChange(1)} disabled={currentPage === 1}>
                            «
                        </button>
                        <button
                            className="pagination-btn"
                            onClick={() => onPageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                        >
                            ‹
                        </button>
                        <span className="pagination-current">
                            {t('bookList.pagination.pageInfo', { current: currentPage, total: totalPages })}
                        </span>
                        <button
                            className="pagination-btn"
                            onClick={() => onPageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                        >
                            ›
                        </button>
                        <button
                            className="pagination-btn"
                            onClick={() => onPageChange(totalPages)}
                            disabled={currentPage === totalPages}
                        >
                            »
                        </button>
                    </div>
                    <div className="pagination-per-page">
                        <label htmlFor="items-per-page">{t('bookList.pagination.perPage')}</label>
                        <select id="items-per-page" value={itemsPerPage} onChange={(e) => onItemsPerPageChange(Number(e.target.value))}>
                            {PER_PAGE_OPTIONS.map((opt) => (
                                <option key={opt} value={opt}>
                                    {opt}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            )}

            {/* Export Bar - Only in list views and NOT shared */}
            {showExport && (
                <div className="list-footer-actions animate-fade-in-up">
                    {/* Import Button */}
                    <button 
                        className="btn-link export-btn" 
                        onClick={onImportClick} 
                        style={{ marginRight: '1rem' }}
                        data-testid="import-books-btn"
                    >
                        📥 {t('bookList.pagination.import')}
                    </button>

                    <div className="export-wrapper">
                        <button
                            className="btn-link export-btn"
                            onClick={() => setShowExportOptions?.(!showExportOptions)}
                            data-testid="export-books-btn"
                        >
                            📥 {t('bookList.pagination.export', { count: selectedCount > 0 ? selectedCount : filteredCount })}
                        </button>

                        {showExportOptions && (
                            <div className="export-options-panel bottom-up animate-fade-in-up" data-testid="export-options-panel">
                                <h4>{t('bookList.pagination.exportFormat')}</h4>
                                <div className="export-options-list">
                                    <label className="export-option" data-testid="export-csv-option">
                                        <input type="radio" name="export-format" onChange={() => onExport?.('csv')} />
                                        <span className="export-label">CSV</span>
                                    </label>
                                    <label className="export-option" data-testid="export-json-option">
                                        <input type="radio" name="export-format" onChange={() => onExport?.('json')} />
                                        <span className="export-label">JSON</span>
                                    </label>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Hidden File Input for Import */}
            {showExport && (
                <input
                    type="file"
                    ref={fileInputRef}
                    onChange={onFileUpload}
                    accept=".csv,.json"
                    style={{ display: 'none' }}
                    data-testid="import-file-input"
                />
            )}
        </>
    )
}
